import { Link } from '@tanstack/react-router';

import { AlertTriangle, Sparkles } from 'lucide-react';

import { Banner } from '@src/components/ui';
import { usePermissions } from '@src/context/hooks';
import { trialDaysLeft } from '@src/corelib/utils/subscriptionUtils';


const COPY = {
  expired: {
    owner: 'Your trial has ended — changes are locked. Upgrade to keep adding and listing parts.',
    member: 'This subscription has expired — changes are locked. Ask your owner to upgrade.',
  },
  past_due: {
    owner: 'Payment is past due — changes are locked. Update billing to restore full access.',
    member: 'Payment is past due — changes are locked. Ask your owner to update billing.',
  },
};


/** App-wide soft-wall banner (#162, #210): reads the active org's subscription.
 *  - expired / past_due: danger banner, writes are blocked server-side
 *    (403 `subscription_locked`).
 *  - trialing (with a trial_ends_at): slim primary/info banner counting down the
 *    remaining days.
 * Owners (canBilling) get an Upgrade link to Billing; everyone else gets plain
 * copy. Mounted once in the /app shell. */
function SubscriptionBanner({ membership }) {
  const { canBilling } = usePermissions();
  const subscription = membership?.seller?.subscription;
  const status = subscription?.effective_status;

  if (status === 'trialing' && subscription?.trial_ends_at) {
    const daysLeft = trialDaysLeft(subscription.trial_ends_at);
    const dayLabel = `${daysLeft} day${daysLeft === 1 ? '' : 's'}`;
    const copy = daysLeft === 0
      ? 'Free trial — last day.'
      : `Free trial — ${dayLabel} left.`;

    return (
      <Banner
        variant="primary"
        icon={<Sparkles />}
        action={canBilling
          ? <Link to="/app/settings/billing">Upgrade</Link>
          : null}
      >
        {copy}
      </Banner>
    );
  }

  if (status !== 'expired' && status !== 'past_due') {
    return null;
  }

  const copy = COPY[status];

  return (
    <Banner
      variant="danger"
      icon={<AlertTriangle />}
      action={canBilling
        ? <Link to="/app/settings/billing">Upgrade</Link>
        : null}
    >
      {canBilling ? copy.owner : copy.member}
    </Banner>
  );
}

SubscriptionBanner.displayName = 'SubscriptionBanner';
export default SubscriptionBanner;
